import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  Button,
  Pagination,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Skeleton,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Phone,
  Message,
  WhatsApp,
  Block,
  Info,
  ArrowBackIos,
  ArrowForwardIos,
  ArrowDropDown,
  Warning,
  FilterList,
  Close,
  Undo,
  Delete,
} from "@mui/icons-material";
import colors from "../constants/colors";
import { URL } from "../constants/url";
import CategoryModal from "../components/CategoryModal";
import NoProductsImage from "../assets/no_products.png";
import NoImagePlaceholder from "../assets/Alternative_image.jpg";


const PLACEHOLDER_IMG = NoImagePlaceholder;

// Skeleton Product Card Component
const SkeletonProductCard = ({ isBanned = false }) => (
  <Card
    sx={{
      width: 280,
      height: isBanned ? 520 : 480,
      mx: "auto",
      borderRadius: 4,
      border: "1px solid rgba(128, 128, 128, 0.2)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      backgroundColor: colors.white,
    }}
  >
    {/* Image Skeleton */}
    <Box
      sx={{
        width: "90%",
        height: 180,
        mt: 2,
        mx: "auto",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#f0f0f0",
      }}
    >
      <Skeleton variant="rectangular" width="100%" height="100%" />
    </Box>

    {/* Card Body Skeleton */}
    <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
      {/* Top Info Skeleton */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Skeleton variant="text" width={80} height={20} />
        <Skeleton variant="rounded" width={60} height={24} />
      </Box>

      {/* Ban Reason Skeleton (only for banned) */}
      {isBanned && (
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="rounded" width="100%" height={60} />
        </Box>
      )}

      {/* Content Details Skeleton */}
      <Box sx={{ flexGrow: 1, mb: 2 }}>
        {[1, 2, 3].map((item) => (
          <Skeleton
            key={item}
            variant="text"
            width="90%"
            height={20}
            sx={{ mt: 1 }}
          />
        ))}

        {/* Price and Age Row Skeleton */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Skeleton variant="text" width={60} height={20} />
          <Skeleton variant="text" width={80} height={30} />
        </Box>
      </Box>

      {/* Action Icons Skeleton */}
      <Stack direction="row" justifyContent="space-around" sx={{ mb: 2 }}>
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} variant="circular" width={40} height={40} />
        ))}
      </Stack>

      {/* Buttons Skeleton */}
      <Stack direction="row" spacing={1}>
        {!isBanned && <Skeleton variant="rounded" width="50%" height={36} />}
        <Skeleton
          variant="rounded"
          width={isBanned ? "100%" : "50%"}
          height={36}
        />
      </Stack>
    </Box>
  </Card>
);

const Market = ({ searchText, onClearSearch,user }) => {
  const [tab, setTab] = useState("Pashu");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 9;
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [banProduct, setBanProduct] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [imageIndex, setImageIndex] = useState({});
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [selectedCategoryType, setSelectedCategoryType] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [bannedProducts, setBannedProducts] = useState([]);
  const [bannedLoading, setBannedLoading] = useState(false);
  const [sortBy, setSortBy] = useState(""); // "asc" or "desc" for price sorting
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryKeywords, setCategoryKeywords] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const productsContainerRef = useRef(null);

const TABS = [
  { label: "Animals", value: "Pashu" },
  { label: "Crops", value: "Crops" },
  { label: "Tools", value: "Aujare" },
  // Only show banned products tab for logged in users
  ...(user ? [{ label: "Banned Products", value: "banned" }] : []),
];

  useEffect(() => {
    // Scroll the products container to top when page changes
    if (productsContainerRef.current) {
      productsContainerRef.current.scrollTop = 0;
    }
  }, [page, tab]);

  // Auto-switch tab when filter category type changes
  useEffect(() => {
    if (selectedFilter?.categoryType && selectedFilter.categoryType !== tab) {
      setTab(selectedFilter.categoryType);
      setPage(1);
    }
  }, [selectedFilter, tab]);

  // Reset to page 1 when search text changes
  useEffect(() => {
    if (searchText) {
      setPage(1);
    }
  }, [searchText]);

  // Fetch all categories to build dynamic keyword mapping
  useEffect(() => {
    fetchAllCategories();
  }, []);

  // Dynamic category detection from search
  useEffect(() => {
    if (
      searchText &&
      searchText.trim() !== "" &&
      Object.keys(categoryKeywords).length > 0
    ) {
      const searchLower = searchText.toLowerCase().trim();

      // Count matches for each category using dynamic keywords
      const categoryMatches = {
        Pashu: 0,
        Crops: 0,
        Aujare: 0,
      };

      // Check search against each category's keywords
      Object.keys(categoryKeywords).forEach((category) => {
        categoryKeywords[category].forEach((keyword) => {
          if (searchLower.includes(keyword)) {
            categoryMatches[category]++;
          }
        });
      });

      // Also check for direct category name matches
      if (searchLower.includes("pashu")) categoryMatches.Pashu += 2;
      if (searchLower.includes("crops")) categoryMatches.Crops += 2;
      if (searchLower.includes("aujare")) categoryMatches.Aujare += 2;

      // Find category with most matches
      const maxCategory = Object.keys(categoryMatches).reduce(
        (max, category) => {
          return categoryMatches[category] > categoryMatches[max]
            ? category
            : max;
        },
        "Pashu"
      );

      // Switch tab if we have strong matches and it's different from current tab
      const strongMatch = categoryMatches[maxCategory] > 0;
      const differentTab = tab !== maxCategory;

      if (strongMatch && differentTab) {
        setTab(maxCategory);
        setPage(1);

        // Clear any existing filter since we're doing a new search
        if (selectedFilter) {
          setSelectedFilter(null);
        }
      }
    }
  }, [searchText, tab, categoryKeywords, selectedFilter]);

  const fetchAllCategories = async () => {
    try {
      const responses = await Promise.all([
        axios.get(`${URL}/api/category/fetch-categories?categoryType=Pashu`),
        axios.get(`${URL}/api/category/fetch-categories?categoryType=Crops`),
        axios.get(`${URL}/api/category/fetch-categories?categoryType=Aujare`),
      ]);

      const categoryMap = {
        Pashu: [],
        Crops: [],
        Aujare: [],
      };

      responses.forEach((res, index) => {
        const categoryType = ["Pashu", "Crops", "Aujare"][index];
        if (res?.data?.success) {
          const categories = res.data.data || [];
          categories.forEach((cat) => {
            cat.subCategories.forEach((sub) => {
              // Add subcategory name
              categoryMap[categoryType].push(sub.name.toLowerCase());

              // Add varieties for animals
              if (categoryType === "Pashu" && sub.variety) {
                sub.variety.forEach((variety) => {
                  categoryMap[categoryType].push(variety.toLowerCase());
                });
              }
            });
          });
        }
      });

      setAvailableCategories(categoryMap);

      // Build keyword mapping for search
      buildKeywordMapping(categoryMap);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const buildKeywordMapping = (categoryMap) => {
    const keywords = {
      Pashu: new Set(),
      Crops: new Set(),
      Aujare: new Set(),
    };

    // Add actual category data
    Object.keys(categoryMap).forEach((category) => {
      categoryMap[category].forEach((item) => {
        keywords[category].add(item);

        // Add partial matches and common variations
        const words = item.split(" ");
        words.forEach((word) => {
          if (word.length > 3) {
            keywords[category].add(word);
          }
        });
      });
    });

    setCategoryKeywords(keywords);
  };

  const queryParams = useMemo(() => {
    const params = {
      page,
      limit,
    };

    // Use current tab as category
    params.category = tab;

    // Apply subCategory filter if selected AND it matches the current tab
    if (
      selectedFilter?.subCategoryName &&
      selectedFilter?.categoryType === tab
    ) {
      params.subCategory = selectedFilter.subCategoryName;
    }

    // Add variety search if selected AND it matches the current tab
    if (selectedFilter?.varietyName && selectedFilter?.categoryType === tab) {
      params.search = selectedFilter.varietyName;
    }

    // Add search text - this will work with the auto-switched tab
    if (searchText && searchText.trim() !== "") {
      params.search = searchText.trim();
    }

    // Add sorting
    if (sortBy && tab !== "banned") {
      params.sort = sortBy;
    }

    return params;
  }, [tab, page, limit, selectedFilter, searchText, sortBy]);

  // Fetch products based on current tab
  useEffect(() => {
    if (tab === "banned") {
      fetchBannedProducts();
    } else {
      fetchRegularProducts();
    }
  }, [queryParams, tab]);

  const fetchRegularProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${URL}/api/sells/get-all-sells`, {
        params: queryParams,
      });
      if (res?.data?.success) {
        // Filter out banned products from regular view
        const activeProducts = (res.data.data || []).filter(
          (product) => !product.isDisabled
        );
        setProducts(activeProducts);
        setTotalPages(res.data.totalPages || 1);
      } else {
        setProducts([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchBannedProducts = async () => {
    setBannedLoading(true);
    try {
      const res = await axios.get(`${URL}/api/admin/banned-products`);
      if (res?.data?.success) {
        setBannedProducts(res.data.data || []);
      } else {
        setBannedProducts([]);
      }
    } catch (error) {
      console.error("Error fetching banned products:", error);
      setBannedProducts([]);
    } finally {
      setBannedLoading(false);
    }
  };

  const handleNextImage = (id, total) => {
    setImageIndex((prev) => ({
      ...prev,
      [id]: ((prev[id] || 0) + 1) % total,
    }));
  };

  const handlePrevImage = (id, total) => {
    setImageIndex((prev) => ({
      ...prev,
      [id]: ((prev[id] || 0) - 1 + total) % total,
    }));
  };

  const handleBanConfirm = async () => {
    try {
      await axios.post(`${URL}/api/admin/manage-product`, {
        productId: banProduct._id,
        reason: banReason,
      });

      // Remove from regular products and add to banned products
      setProducts((prev) => prev.filter((p) => p._id !== banProduct._id));

      // Add to banned products with the ban reason
      const bannedProductWithReason = {
        ...banProduct,
        isDisabled: true,
        productStatus: "Banned by Admin",
        disabledReason: banReason,
      };
      setBannedProducts((prev) => [bannedProductWithReason, ...prev]);

      setBanProduct(null);
      setBanReason("");
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteProduct = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete || !productToDelete._id) {
      console.error("Cannot delete: productToDelete is null or missing _id", productToDelete);
      setDeleteDialogOpen(false);
      setDialogLoading(false);
      return;
    }

    setDialogLoading(true);
    try {
      await axios.delete(`${URL}/api/sells/delete-sell/${productToDelete._id}`);
      
      // Remove from current products list
      if (tab === "banned") {
        setBannedProducts((prev) => prev.filter((p) => p._id !== productToDelete._id));
      } else {
        setProducts((prev) => prev.filter((p) => p._id !== productToDelete._id));
      }
      
      // Close dialog properly
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    } catch (error) {
      console.error("Error deleting product:", error);
      // Keep dialog open on error so user can try again
    } finally {
      setDialogLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  //const handleMessage = (phone) => window.open(`sms:${phone}`, "_self");

  const handleCategorySelect = (data) => {
    console.log("Category selected:", data);
    setSelectedFilter({
      ...data,
      categoryType: selectedCategoryType,
    });
    setPage(1);
    setCategoryModalOpen(false);
  };

  const clearFilter = () => {
    setSelectedFilter(null);
    setPage(1);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setPage(1);
    // Clear filter only if user manually changes tab to a non-banned tab
    if (
      selectedFilter &&
      selectedFilter.categoryType !== newValue &&
      newValue !== "banned"
    ) {
      setSelectedFilter(null);
    }
    // Reset sorting when switching tabs
    if (newValue === "banned") {
      setSortBy("");
    }
  };

  const handleDropdownClick = (tabValue, event) => {
    event.stopPropagation();
    // Don't open category modal for banned tab
    if (tabValue === "banned") return;
    setCategoryModalOpen(true);
    setSelectedCategoryType(tabValue);
  };

  // Filter menu handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
    handleFilterClose();
  };

  const clearSort = () => {
    setSortBy("");
    setPage(1);
    handleFilterClose();
  };

  const getCategoryDetails = (product) => {
    const category = product.category;
    if (category === "Pashu") {
      const d = product.pashuDetails || {};
      return [
        {
          label: "Animal",
          value:
            product.subCategory === "Other"
              ? product.otherSubCategory
              : product.subCategory,
        },
        { label: "Variety", value: d.variety },
        { label: "Address", value: product.shortAddress },
      ];
    } else if (category === "Crops") {
      const d = product.cropDetails || {};
      return [
        { label: "Crop", value: product.subCategory },
        { label: "Quantity", value: d.quantity },
        { label: "Address", value: product.shortAddress },
      ];
    } else if (category === "Aujare") {
      const d = product.aujareDetails || {};
      return [
        { label: "Tool", value: product.subCategory },
        { label: "Model Year", value: d.modelYear },
        { label: "Address", value: product.shortAddress },
      ];
    }
    return [];
  };

  // Helper function to format field names
  const formatFieldName = (fieldName) => {
    const fieldMap = {
      // Pashu Details
      variety: "Variety",
      todayMilkCapacity: "Today's Milk Capacity",
      highestMilkCapacity: "Highest Milk Capacity",
      behavior: "Behavior",
      lactation: "Lactation",
      otherInfo: "Additional Information",

      // Crop Details
      quantity: "Quantity",
      otherInfo: "Additional Information",

      // Aujare Details
      modelNumber: "Model Number",
      modelYear: "Model Year",
      companyName: "Company Name",
      ownerHistory: "Owner History",
      condition: "Condition",
      otherInfo: "Additional Information",
    };

    return (
      fieldMap[fieldName] ||
      fieldName
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
    );
  };

  const getFilterDisplayText = () => {
    if (!selectedFilter) return "";

    if (selectedFilter.varietyName) {
      return `${selectedFilter.subCategoryName} - ${selectedFilter.varietyName}`;
    }

    return selectedFilter.subCategoryName || "";
  };

  // Get current products based on tab
  const currentProducts = tab === "banned" ? bannedProducts : products;
  const currentLoading = tab === "banned" ? bannedLoading : loading;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.darkWhite,
      }}
    >
      {/* ✅ Fixed Tab Section with Filter Display */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: `linear-gradient(135deg, rgba(200, 245, 210, 0.8), rgba(230, 255, 230, 0.9))`,
          backdropFilter: "blur(6px)",
          borderBottom: `2px solid ${colors.darkGreen}`,
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{
            style: {
              display: "none",
            },
          }}
          sx={{
            "& .MuiTabs-flexContainer": {
              justifyContent: "space-evenly",
              position: "relative",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1.1rem",
              color: "#555",
              px: 3,
              mt: 2,
              transition: "color 0.3s ease, font-weight 0.3s ease",
              "&.Mui-selected": {
                color: tab === "banned" ? colors.red : colors.darkGreen,
                fontWeight: 700,
              },
              minHeight: "48px",
            },
          }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.value}
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {t.label}
                  {t.value !== "banned" && (
                    <IconButton
                      size="small"
                      onClick={(event) => handleDropdownClick(t.value, event)}
                      sx={{
                        ml: 0.5,
                        color: "inherit",
                        padding: "4px",
                        "&:hover": {
                          backgroundColor: "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      <ArrowDropDown sx={{ fontSize: "20px" }} />
                    </IconButton>
                  )}
                </Box>
              }
              value={t.value}
            />
          ))}
        </Tabs>
      </Box>

      {/* Product Grid */}
      {/* Product Grid */}
      <Box
        ref={productsContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 4,
          py: 4,
          // "&::-webkit-scrollbar": { display: "none" },
          pb: 10,
          backgroundColor: colors.darkWhite,
          position: "relative",
        }}
      >
        {/* Filter Section with Chip on Left and Filter Button on Right */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            mt: -2.5,
          }}
        >
          {/* Category Filter Chip - LEFT SIDE */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {selectedFilter && tab !== "banned" && (
              <Chip
                label={`Filter: ${getFilterDisplayText()}`}
                onDelete={clearFilter}
                deleteIcon={<Close />}
                sx={{
                  backgroundColor: colors.darkGreen,
                  color: colors.white,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  px: 1,
                  "& .MuiChip-deleteIcon": {
                    color: colors.white,
                    "&:hover": {
                      color: colors.lightMint,
                    },
                  },
                }}
              />
            )}
          </Box>

          {/* Filter Button - RIGHT SIDE */}
          {tab !== "banned" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                startIcon={<FilterList />}
                onClick={handleFilterClick}
                variant="outlined"
                sx={{
                  borderColor: colors.green,
                  color: colors.darkGreen,
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  "&:hover": {
                    backgroundColor: colors.lightMint,
                    borderColor: colors.darkGreen,
                  },
                }}
              >
                Filter
              </Button>
              <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={handleFilterClose}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    mt: 1,
                    minWidth: 220,
                    overflow: "hidden",
                    border: `1px solid ${colors.lightGray}`,
                  },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: colors.lightMint,
                    borderBottom: `1px solid ${colors.green}20`,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: colors.darkGreen,
                      fontSize: "0.9rem",
                    }}
                  >
                    Sort by Price
                  </Typography>
                </Box>

                {/* Sort Options */}
                <MenuItem
                  onClick={() => handleSortChange("asc")}
                  selected={sortBy === "asc"}
                  sx={{
                    py: 1.5,
                    px: 2,
                    fontWeight: sortBy === "asc" ? 700 : 500,
                    color:
                      sortBy === "asc" ? colors.darkGreen : colors.darkBlack,
                    backgroundColor:
                      sortBy === "asc" ? colors.lightMint : "transparent",
                    borderBottom: `1px solid ${colors.lightGray}`,
                    "&:hover": {
                      backgroundColor: colors.lightMint,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor:
                          sortBy === "asc" ? colors.darkGreen : "transparent",
                        border:
                          sortBy === "asc"
                            ? "none"
                            : `2px solid ${colors.darkGray}`,
                        mr: 2,
                      }}
                    />
                    Price: Low to High
                  </Box>
                </MenuItem>

                <MenuItem
                  onClick={() => handleSortChange("desc")}
                  selected={sortBy === "desc"}
                  sx={{
                    py: 1.5,
                    px: 2,
                    fontWeight: sortBy === "desc" ? 700 : 500,
                    color:
                      sortBy === "desc" ? colors.darkGreen : colors.darkBlack,
                    backgroundColor:
                      sortBy === "desc" ? colors.lightMint : "transparent",
                    borderBottom: `1px solid ${colors.lightGray}`,
                    "&:hover": {
                      backgroundColor: colors.lightMint,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor:
                          sortBy === "desc" ? colors.darkGreen : "transparent",
                        border:
                          sortBy === "desc"
                            ? "none"
                            : `2px solid ${colors.darkGray}`,
                        mr: 2,
                      }}
                    />
                    Price: High to Low
                  </Box>
                </MenuItem>

                {/* Action Buttons */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#fafafa",
                    borderTop: `1px solid ${colors.lightGray}`,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={handleFilterClose}
                    sx={{
                      borderColor: colors.darkGray,
                      color: colors.darkGray,
                      fontWeight: 600,
                      borderRadius: 2,
                      fontSize: "0.8rem",
                      py: 0.8,
                      "&:hover": {
                        borderColor: colors.darkGray,
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  {sortBy && (
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={clearSort}
                      sx={{
                        backgroundColor: colors.red,
                        fontWeight: 600,
                        borderRadius: 2,
                        fontSize: "0.8rem",
                        py: 0.8,
                        "&:hover": {
                          backgroundColor: "#d32f2f",
                        },
                      }}
                    >
                      Reset
                    </Button>
                  )}
                </Box>
              </Menu>
            </Box>
          )}
        </Box>

        {/* IMPROVED LOADING STATE WITH SKELETON */}
        {currentLoading ? (
          <Grid container spacing={4} justifyContent="center">
            {Array.from({ length: limit }).map((_, index) => (
              <Grid item key={index} xs={12} sm={6} md={4}>
                <SkeletonProductCard isBanned={tab === "banned"} />
              </Grid>
            ))}
          </Grid>
        ) : currentProducts.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              px: 2,
            }}
          >
            {/* No Products Image */}
            <Box
              component="img"
              src={NoProductsImage}
              alt="No products found"
              sx={{
                width: 230,
                height: 230,
                mt: -3,
              }}
            />

            {/* Message based on context */}
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                color: colors.darkGray,
                fontWeight: 600,
                mb: 1,
              }}
            >
              {tab === "banned"
                ? "No Banned Products"
                : searchText
                ? `No results for "${searchText}"`
                : selectedFilter
                ? "No matching products"
                : "No Products Available"}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: colors.darkGray,
                maxWidth: 400,
              }}
            >
              {tab === "banned"
                ? "There are no banned products at the moment."
                : searchText
                ? "Try adjusting your search terms or browse different categories."
                : selectedFilter
                ? "Try changing your filter criteria or browse all products."
                : "Check back later for new products or try different categories."}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4} justifyContent="center">
            {currentProducts.map((product) => {
              const imgs = product.images?.length
                ? product.images.map((img) =>
                    img.startsWith("http")
                      ? img
                      : `${URL.replace(/\/api\/?$/, "")}/${img.replace(
                          /^\/+/,
                          ""
                        )}`
                  )
                : [PLACEHOLDER_IMG];
              const idx = imageIndex[product._id] || 0;
              const isBanned = product.isDisabled || tab === "banned";

              return (
                <Grid item key={product._id} xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      width: 280,
                      height: isBanned ? 580 : 480,
                      mx: "auto",
                      borderRadius: 4,
                      border: `1px solid ${
                        isBanned ? colors.red : "rgba(128, 128, 128, 0.5)"
                      }`,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "0.3s ease",
                      backgroundColor: isBanned ? "#fff5f5" : colors.white,
                      boxShadow: isBanned
                        ? `0 4px 12px ${colors.red}20`
                        : "none",
                      minHeight: isBanned ? 580 : 480,
                      maxHeight: isBanned ? 650 : 550,
                    }}
                  >
                    {/* Inner Image Box */}
                    <Box
                      sx={{
                        width: "90%",
                        height: 180,
                        mt: 2,
                        mx: "auto",
                        borderRadius: 3,
                        overflow: "hidden",
                        position: "relative",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        opacity: isBanned ? 0.7 : 1,
                      }}
                    >
                      <img
                        src={imgs[idx]}
                        alt="product"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {imgs.length > 1 && (
                        <>
                          {/* Previous Button */}
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: 8,
                              transform: "translateY(-50%)",
                              color: isBanned ? colors.red : colors.green,
                              backgroundColor: "rgba(255, 255, 255, 0.25)",
                              backdropFilter: "blur(8px)",
                              border: "1px solid rgba(255, 255, 255, 0.3)",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.4)",
                                transform: "translateY(-50%) scale(1.15)",
                                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
                                color: isBanned ? "#d32f2f" : colors.darkGreen,
                              },
                              transition: "all 0.3s ease",
                              width: "36px",
                              height: "36px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrevImage(product._id, imgs.length);
                            }}
                          >
                            <ArrowBackIos
                              fontSize="small"
                              sx={{
                                fontSize: "18px",
                                marginLeft: "2px",
                                filter:
                                  "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                              }}
                            />
                          </IconButton>

                          {/* Next Button */}
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              right: 8,
                              transform: "translateY(-50%)",
                              color: isBanned ? colors.red : colors.green,
                              backgroundColor: "rgba(255, 255, 255, 0.25)",
                              backdropFilter: "blur(8px)",
                              border: "1px solid rgba(255, 255, 255, 0.3)",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.4)",
                                transform: "translateY(-50%) scale(1.15)",
                                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
                                color: isBanned ? "#d32f2f" : colors.darkGreen,
                              },
                              transition: "all 0.3s ease",
                              width: "36px",
                              height: "36px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNextImage(product._id, imgs.length);
                            }}
                          >
                            <ArrowForwardIos
                              fontSize="small"
                              sx={{
                                fontSize: "18px",
                                filter:
                                  "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                              }}
                            />
                          </IconButton>
                        </>
                      )}
                    </Box>

                    {/* Card Body */}
                    <Box
                      sx={{
                        p: 2,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        color: colors.darkBlack,
                        fontWeight: 600,
                      }}
                    >
                      {/* Top Info */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color={colors.darkGray}
                          fontSize="1rem"
                          fontWeight={400}
                        >
                          {formatDate(product.createdAt)}
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: isBanned
                              ? colors.red
                              : colors.green,
                            color: colors.white,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: "0.9rem",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isBanned ? "Banned" : "For Sale"}
                        </Box>
                      </Box>

                      {/* Ban Reason - Only show for banned products */}
                      {isBanned && product.disabledReason && (
                        <Box
                          sx={{
                            backgroundColor: "#ffeaea",
                            border: `1px solid ${colors.red}`,
                            borderRadius: 1,
                            p: 1,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Warning
                              sx={{
                                fontSize: "1rem",
                                color: colors.red,
                                mr: 0.5,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: colors.red,
                                fontSize: "0.8rem",
                              }}
                            >
                              Ban Reason:
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.darkGray,
                              fontSize: "0.75rem",
                              lineHeight: 1.2,
                            }}
                          >
                            {product.disabledReason}
                          </Typography>
                        </Box>
                      )}

                      {/* Content Area */}
                      <Box
                        sx={{
                          flexGrow: 1,
                          mb: 1,
                          display: "flex",
                          flexDirection: "column",
                          minHeight: 120,
                          overflow: "hidden",
                        }}
                      >
                        {getCategoryDetails(product).map((d) => (
                          <Typography
                            key={d.label}
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              fontSize: "0.9rem",
                              color: colors.darkBlack,
                              lineHeight: 1.3,
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-start",
                              flexWrap: "wrap",
                            }}
                          >
                            {d.label !== "Offer Price" && (
                              <>
                                <Box
                                  component="span"
                                  sx={{
                                    fontWeight: 600,
                                    marginRight: "4px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {d.label}:
                                </Box>
                                <Box
                                  component="span"
                                  sx={{
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {d.value || "N/A"}
                                </Box>
                              </>
                            )}
                          </Typography>
                        ))}

                        {/* Price and Age Row */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          {/* Age on left - "Age:" in black, value in grey */}
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: colors.darkBlack,
                                fontSize: "0.9rem",
                                mr: 0.5,
                              }}
                            >
                              Age:
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.9rem",
                              }}
                            >
                              {product.age || "N/A"}
                            </Typography>
                          </Box>

                          {/* Price on right - big green text */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: isBanned
                                ? colors.darkGray
                                : colors.darkGreen,
                              fontSize: "1.5rem",
                              textDecoration: isBanned
                                ? "line-through"
                                : "none",
                              opacity: isBanned ? 0.6 : 1,
                            }}
                          >
                            ₹{product.offerPrice}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Action Icons with enhanced shadows */}
                      <Stack
                        direction="row"
                        justifyContent="space-around"
                        sx={{
                          mb: 1.5,
                          "& .MuiIconButton-root": {
                            color: isBanned ? colors.red : colors.green,
                            background: isBanned
                              ? `linear-gradient(135deg, rgba(255, 230, 230, 0.9), rgba(255, 200, 200, 0.9))`
                              : `linear-gradient(135deg, rgba(200, 245, 210, 0.9), rgba(230, 255, 230, 0.9))`,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            border: "1px solid #e0e0e0",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: isBanned
                                ? "#ffebee"
                                : colors.hoverMint,
                              transform: "scale(1.1)",
                              boxShadow: isBanned
                                ? "0 6px 16px rgba(211, 47, 47, 0.3)"
                                : "0 6px 16px rgba(27, 138, 46, 0.5)",
                            },
                          },
                        }}
                      >
                        <IconButton
                          onClick={() =>
                            window.open(`tel:${product.userPhone}`, "_self")
                          }
                        >
                          <Phone />
                        </IconButton>
                        {/* <IconButton
                          onClick={() => handleMessage(product.userPhone)}
                        >
                          <Message />
                        </IconButton> */}
                        <IconButton
                          onClick={() =>
                            window.open(
                              `https://wa.me/${product.userPhone.replace(
                                "+",
                                ""
                              )}`
                            )
                          }
                        >
                          <WhatsApp />
                        </IconButton>
                        {/* Delete Icon - Only show for admin users */}
                        {user && user.role === "admin" && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProduct(product);
                            }}
                            sx={{
                              color: "#d32f2f !important",
                              background: `linear-gradient(135deg, rgba(211, 47, 47, 0.15), rgba(198, 40, 40, 0.25)) !important`,
                              border: "2px solid rgba(211, 47, 47, 0.3) !important",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.2) !important",
                                color: "#d32f2f !important",
                                border: "2px solid rgba(211, 47, 47, 0.5) !important",
                                boxShadow: "0 6px 16px rgba(211, 47, 47, 0.4) !important",
                              },
                            }}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Stack>

                      {/* Buttons with enhanced shadows */}
                      <Stack direction="row" spacing={1}>
                        {!isBanned && user && (
                          <Button
                            fullWidth
                            startIcon={<Block />}
                            variant="contained"
                            color="error"
                            onClick={() => setBanProduct(product)}
                            sx={{
                              boxShadow: "0 3px 8px rgba(211, 47, 47, 0.3)",
                              "&:hover": {
                                boxShadow: "0 5px 12px rgba(211, 47, 47, 0.4)",
                              },
                            }}
                          >
                            Ban
                          </Button>
                        )}

                        {isBanned && user && (
                          <Button
                            fullWidth
                            startIcon={<Undo />}
                            variant="contained"
                            color="success"
                            onClick={async () => {
                              try {
                                await axios.post(`${URL}/api/admin/manage-product`, {
                                  productId: product._id,
                                  action: "unban",
                                });

                                // Remove from banned list and add back to active products
                                setBannedProducts((prev) => prev.filter((p) => p._id !== product._id));
                                setProducts((prev) => [ { ...product, isDisabled: false, disabledReason: "", productStatus: "Available" }, ...prev ]);
                              } catch (err) {
                                console.error("Unban failed:", err);
                              }
                            }}
                            sx={{
                              boxShadow: "0 3px 8px rgba(46, 125, 50, 0.2)",
                              "&:hover": {
                                boxShadow: "0 5px 12px rgba(46, 125, 50, 0.3)",
                              },
                            }}
                          >
                            Unban
                          </Button>
                        )}                        <Button
                          fullWidth
                          startIcon={<Info />}
                          variant="outlined"
                          onClick={() => setSelectedProduct(product)}
                          sx={{
                            borderColor: isBanned ? colors.red : colors.green,
                            color: isBanned ? colors.red : colors.green,
                            boxShadow: isBanned
                              ? "0 3px 8px rgba(211, 47, 47, 0.2)"
                              : "0 3px 8px rgba(76, 175, 80, 0.2)",
                            "&:hover": {
                              backgroundColor: isBanned
                                ? "#ffebee"
                                : colors.lightMint,
                              boxShadow: isBanned
                                ? "0 5px 12px rgba(211, 47, 47, 0.3)"
                                : "0 5px 12px rgba(76, 175, 80, 0.3)",
                            },
                          }}
                        >
                          View
                        </Button>
                      </Stack>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Pagination - Only show for regular products */}
        {tab !== "banned" && totalPages > 1 && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#2c3e50",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                  minWidth: "42px",
                  height: "42px",
                  margin: "0 4px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  },
                },
                "& .MuiPaginationItem-page": {
                  "&.Mui-selected": {
                    backgroundColor: colors.darkGreen,
                    color: colors.white,
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    border: `2px solid ${colors.darkGreen}`,
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      backgroundColor: colors.green,
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                    },
                  },
                },
                "& .MuiPaginationItem-ellipsis": {
                  fontSize: "1.2rem",
                  fontWeight: 600,
                  color: "#666",
                },
                "& .MuiPaginationItem-previousNext": {
                  border: `2px solid ${colors.green}`,
                  backgroundColor: "transparent",
                  color: colors.green,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: colors.green,
                    color: colors.white,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                  },
                  "&.Mui-disabled": {
                    border: "1px solid #e0e0e0",
                    color: "#bdbdbd",
                    backgroundColor: "#fafafa",
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* View Modal */}
      <Dialog
        open={Boolean(selectedProduct)}
        onClose={() => setSelectedProduct(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: selectedProduct?.isDisabled ? colors.red : colors.darkGreen,
            fontSize: "1.3rem",
            borderBottom: `1px solid ${colors.lightGray}`,
            pb: 2,
          }}
        >
          {selectedProduct?.title || "Product Details"}
          {selectedProduct?.isDisabled && (
            <Chip
              label="BANNED"
              size="small"
              sx={{
                ml: 2,
                backgroundColor: colors.red,
                color: colors.white,
                fontWeight: 700,
                fontSize: "0.7rem",
              }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {/* Ban Reason Section - Only show if product is banned */}
          {selectedProduct?.isDisabled && selectedProduct?.disabledReason && (
            <Box
              sx={{
                backgroundColor: "#fff5f5",
                border: `1px solid ${colors.red}`,
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Warning sx={{ color: colors.red, mr: 1 }} />
                <Typography
                  variant="h6"
                  sx={{ color: colors.red, fontWeight: 600 }}
                >
                  Ban Reason
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: colors.darkGray }}>
                {selectedProduct.disabledReason}
              </Typography>
            </Box>
          )}

          {/* Owner Information */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: selectedProduct?.isDisabled
                ? colors.red
                : colors.darkGreen,
            }}
          >
            Owner Information
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Name:</strong> {selectedProduct?.ownerName || "N/A"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Phone:</strong> {selectedProduct?.userPhone || "N/A"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Address:</strong> {selectedProduct?.address || "N/A"}
            </Typography>
          </Box>

          {/* Basic Information */}
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: selectedProduct?.isDisabled
                ? colors.red
                : colors.darkGreen,
            }}
          >
            Basic Information
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Category:</strong> {selectedProduct?.category || "N/A"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Sub Category:</strong>{" "}
              {selectedProduct?.subCategory === "Other"
                ? selectedProduct?.otherSubCategory
                : selectedProduct?.subCategory || "N/A"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Age:</strong> {selectedProduct?.age || "N/A"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Offer Price:</strong>{" "}
              <span
                style={{
                  color: selectedProduct?.isDisabled
                    ? colors.darkGray
                    : colors.green,
                  fontWeight: 700,
                  textDecoration: selectedProduct?.isDisabled
                    ? "line-through"
                    : "none",
                }}
              >
                ₹{selectedProduct?.offerPrice || "N/A"}
              </span>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong>{" "}
              <span
                style={{
                  color: selectedProduct?.isDisabled
                    ? colors.red
                    : colors.green,
                  fontWeight: 600,
                }}
              >
                {selectedProduct?.isDisabled ? "Banned" : "Available"}
              </span>
            </Typography>
            <Typography variant="body2">
              <strong>Posted:</strong> {formatDate(selectedProduct?.createdAt)}
            </Typography>
          </Box>

          {/* Category Specific Details */}
          {selectedProduct && (
            <>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: selectedProduct?.isDisabled
                    ? colors.red
                    : colors.darkGreen,
                }}
              >
                {selectedProduct.category} Details
              </Typography>
              <Box sx={{ mb: 2 }}>
                {selectedProduct.category === "Pashu" &&
                  selectedProduct.pashuDetails &&
                  Object.entries(selectedProduct.pashuDetails).map(
                    ([key, value]) => (
                      <Typography key={key} variant="body2" sx={{ mb: 1 }}>
                        <strong>{formatFieldName(key)}:</strong>{" "}
                        {value || "Not specified"}
                      </Typography>
                    )
                  )}

                {selectedProduct.category === "Crops" &&
                  selectedProduct.cropDetails &&
                  Object.entries(selectedProduct.cropDetails).map(
                    ([key, value]) => (
                      <Typography key={key} variant="body2" sx={{ mb: 1 }}>
                        <strong>{formatFieldName(key)}:</strong>{" "}
                        {value || "Not specified"}
                      </Typography>
                    )
                  )}

                {selectedProduct.category === "Aujare" &&
                  selectedProduct.aujareDetails &&
                  Object.entries(selectedProduct.aujareDetails).map(
                    ([key, value]) => (
                      <Typography key={key} variant="body2" sx={{ mb: 1 }}>
                        <strong>{formatFieldName(key)}:</strong>{" "}
                        {value || "Not specified"}
                      </Typography>
                    )
                  )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setSelectedProduct(null)}
            sx={{
              borderColor: selectedProduct?.isDisabled
                ? colors.red
                : colors.green,
              color: selectedProduct?.isDisabled ? colors.red : colors.green,
              "&:hover": {
                backgroundColor: selectedProduct?.isDisabled
                  ? "#ffebee"
                  : colors.lightMint,
              },
            }}
          >
            Close
          </Button>
          {!selectedProduct?.isDisabled && user &&(
            <Button
              variant="contained"
              startIcon={<Block />}
              color="error"
              onClick={() => {
                setBanProduct(selectedProduct);
                setSelectedProduct(null);
              }}
            >
              Ban Product
            </Button>
          )}

          {selectedProduct?.isDisabled && user && (
            <Button
              variant="contained"
              startIcon={<Undo />}
              color="success"
              onClick={async () => {
                try {
                  await axios.post(`${URL}/api/admin/manage-product`, {
                    productId: selectedProduct._id,
                    action: "unban",
                  });

                  // Update lists
                  setBannedProducts((prev) => prev.filter((p) => p._id !== selectedProduct._id));
                  setProducts((prev) => [{ ...selectedProduct, isDisabled: false, disabledReason: "", productStatus: "Available" }, ...prev]);
                  setSelectedProduct(null);
                } catch (err) {
                  console.error("Failed to unban product:", err);
                }
              }}
            >
              Unban Product
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Ban Modal */}
      <Dialog
        open={Boolean(banProduct)}
        onClose={() => setBanProduct(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            color: colors.white,
            backgroundColor: colors.red,
            fontSize: "1.25rem",
            py: 2,
            gap: 1,
          }}
        >
          <Block sx={{ fontSize: "1.5rem" }} />
          Ban Product
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 2, textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: colors.darkGray, mb: 0.5 }}
            >
              You are banning:
              <span style={{ color: colors.darkBlack }}>
                {" "}
                "{banProduct?.title}"
              </span>
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.darkGray, fontStyle: "italic" }}
            >
              Owner: {banProduct?.ownerName}
            </Typography>
          </Box>

          <TextField
            label="Reason for Ban"
            fullWidth
            multiline
            minRows={4}
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            placeholder="Enter reason for banning this product..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "0.95rem",
                "&:hover fieldset": { borderColor: colors.red },
                "&.Mui-focused fieldset": {
                  borderColor: colors.red,
                  borderWidth: "2px",
                },
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.95rem",
                fontWeight: 500,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: colors.red,
                fontWeight: 600,
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 2,
              p: 1.5,
              backgroundColor: "#fff5f5",
              borderRadius: 1,
              border: `1px solid ${colors.lightGray}`,
            }}
          >
            <Block sx={{ fontSize: "1.2rem", color: colors.red }} />
            <Typography
              variant="caption"
              sx={{ color: colors.darkGray, fontWeight: 500 }}
            >
              This action cannot be undone. The product will be hidden from
              public view.
            </Typography>
          </Box>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            p: 2.5,
            gap: 2,
            borderTop: `1px solid ${colors.lightGray}`,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setBanProduct(null);
              setBanReason("");
            }}
            sx={{
              borderColor: colors.darkGray,
              color: colors.darkGray,
              borderRadius: 2,
              px: 4,
              py: 1,
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": {
                backgroundColor: "#f8f9fa",
                borderColor: colors.darkGray,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Block />}
            onClick={handleBanConfirm}
            disabled={!banReason.trim()}
            sx={{
              backgroundColor: colors.red,
              borderRadius: 2,
              px: 4,
              py: 1,
              fontWeight: 600,
              fontSize: "0.95rem",
              boxShadow: "0 4px 10px rgba(211,47,47,0.25)",
              "&:hover": {
                backgroundColor: "#d32f2f",
                boxShadow: "0 6px 14px rgba(211,47,47,0.35)",
              },
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
                color: "#bdbdbd",
                boxShadow: "none",
              },
            }}
          >
            Confirm Ban
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!dialogLoading) {
            setDeleteDialogOpen(false);
            setProductToDelete(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
          >
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            Are you sure you want to delete this product "{productToDelete?.title || productToDelete?.subCategory}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 2 },
            px: 3,
            pb: 3,
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setProductToDelete(null);
            }}
            disabled={dialogLoading}
            fullWidth={true}
            size="medium"
            sx={{
              flex: 1,
              minWidth: 100,
              py: 1.5,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={dialogLoading}
            fullWidth={true}
            sx={{
              background: colors.red,
              "&:hover": { background: colors.darkRed },
              flex: 1,
              minWidth: 100,
              py: 1.5,
            }}
            size="medium"
          >
            {dialogLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Modal */}
      <CategoryModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        categoryType={selectedCategoryType}
        onSelect={handleCategorySelect}
      />
    </Box>
  );
};

export default Market;
